import { CircleHelp, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { LIVE_SESSION_TUTORIAL_STEPS } from '@/components/conclave/conclave-live-tutorial';

export function ConclaveLiveHelpButton({
  tutorialMode,
  onToggle,
}: {
  tutorialMode: boolean;
  onToggle: () => void;
}) {
  return (
    <Button
      type="button"
      variant={tutorialMode ? 'default' : 'outline'}
      size="icon"
      className={cn(
        'h-9 w-9 shrink-0 rounded-xl transition-shadow',
        tutorialMode && 'shadow-primary/25 shadow-md'
      )}
      onClick={onToggle}
      aria-label={tutorialMode ? 'Exit live session tutorial' : 'Live session tutorial'}
      title={tutorialMode ? 'Exit tutorial' : 'How live session works'}
    >
      <CircleHelp className="h-5 w-5" />
    </Button>
  );
}

export function ConclaveLiveTutorialCoach({
  stepIndex,
  onStepIndexChange,
  onClose,
}: {
  stepIndex: number;
  onStepIndexChange: (index: number) => void;
  onClose: () => void;
}) {
  const step = LIVE_SESSION_TUTORIAL_STEPS[stepIndex];
  const last = stepIndex >= LIVE_SESSION_TUTORIAL_STEPS.length - 1;
  const total = LIVE_SESSION_TUTORIAL_STEPS.length;
  const progress = ((stepIndex + 1) / total) * 100;
  const Icon = step.icon;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="pointer-events-none fixed inset-x-0 bottom-0 z-40"
    >
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-72 bg-gradient-to-t from-background via-background/90 to-transparent md:h-64"
        aria-hidden
      />

      <div className="relative flex justify-center px-3 pb-[calc(72px+0.75rem)] pt-8 sm:px-4 md:pb-5">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 16 }}
          transition={{ type: 'spring', stiffness: 380, damping: 28 }}
          className="pointer-events-auto w-full max-w-lg overflow-hidden rounded-2xl border-2 border-border bg-card shadow-[0_16px_48px_-12px_rgba(0,0,0,0.4)] ring-1 ring-black/5"
        >
          <div className="h-1 w-full bg-muted">
            <motion.div
              className="h-full bg-gradient-to-r from-primary to-primary/70"
              initial={false}
              animate={{ width: `${progress}%` }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            />
          </div>

          <div className="bg-card p-4 sm:p-5">
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary/80 text-primary-foreground shadow-md shadow-primary/25">
                <AnimatePresence mode="wait">
                  <motion.span
                    key={step.id}
                    initial={{ opacity: 0, scale: 0.7 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.7 }}
                    transition={{ duration: 0.15 }}
                    className="flex"
                  >
                    <Icon className="h-5 w-5" />
                  </motion.span>
                </AnimatePresence>
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Guide · {stepIndex + 1}/{total}
                  </p>
                  <button
                    type="button"
                    onClick={onClose}
                    className="-mr-1 -mt-1 flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    aria-label="Close tutorial"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                <AnimatePresence mode="wait">
                  <motion.div
                    key={step.id}
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -10 }}
                    transition={{ duration: 0.18 }}
                  >
                    <p className="mt-0.5 text-base font-semibold leading-snug text-foreground">
                      {step.title}
                    </p>
                    <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                      {step.body}
                    </p>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-9 rounded-xl px-3"
                disabled={stepIndex === 0}
                onClick={() => onStepIndexChange(stepIndex - 1)}
              >
                <ChevronLeft className="mr-0.5 h-4 w-4" />
                Back
              </Button>

              <div className="flex flex-1 justify-center gap-1.5">
                {LIVE_SESSION_TUTORIAL_STEPS.map((s, i) => (
                  <button
                    key={s.id}
                    type="button"
                    aria-label={`Go to step ${i + 1}`}
                    onClick={() => onStepIndexChange(i)}
                    className={cn(
                      'h-1.5 rounded-full transition-all',
                      i === stepIndex
                        ? 'w-5 bg-primary'
                        : i < stepIndex
                          ? 'w-1.5 bg-primary/40'
                          : 'w-1.5 bg-muted-foreground/25 hover:bg-muted-foreground/40'
                    )}
                  />
                ))}
              </div>

              <Button
                type="button"
                size="sm"
                className="h-9 rounded-xl px-4 shadow-sm shadow-primary/20"
                onClick={() => (last ? onClose() : onStepIndexChange(stepIndex + 1))}
              >
                {last ? 'Got it' : 'Next'}
                {!last ? <ChevronRight className="ml-0.5 h-4 w-4" /> : null}
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}
